/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { getPositionTime } from '../../PRIVATE_TimeComponent/utils/timeUtils';
import { useSelection } from '../../hooks/PRIVATE_useVisSelection';
import type {
  GanttCurrentTask,
  GanttRow,
  GanttSelection as GanttSelectionProps,
  GanttTask,
  GanttTaskHover
} from '../Gantt.types';
import { type ViewportLayout, supportsSelection } from '../utils/dataLayoutUtils';
import { queryTasksSpatialIndex } from '../utils/spatialUtils';

type UseTaskPointerGestureOptions<
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
> = {
  viewportLayout: ViewportLayout<K1, K2, D1, D2>;
  viewportYOffset: number;
  dataBodyRegion: { x: number; y: number; w: number; h: number };
  viewportStartTime: number;
  viewportEndTime: number;
  width: number;
  isRTL: boolean;
  selectionProps: GanttSelectionProps<K2>;
  hoverProps: GanttTaskHover<K2>;
  currentProps: GanttCurrentTask<K2>;
  onCursorChange?: (detail: { cursor: 'pointer' | 'pointerUnset' }) => void;
};

/**
 * Hook that handles task pointer gestures.
 */
const useGanttTaskPointerGestures = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>({
  viewportLayout,
  viewportYOffset,
  dataBodyRegion,
  viewportStartTime,
  viewportEndTime,
  width,
  isRTL,
  selectionProps,
  hoverProps,
  currentProps,
  onCursorChange
}: UseTaskPointerGestureOptions<K1, K2, D1, D2>) => {
  const { x: dbx, y: dby, w: dbw, h: dbh } = dataBodyRegion;
  const selectionSupported = supportsSelection(selectionProps.selectionMode);

  const idExtracter = (event: Event) => {
    const rowObjs = viewportLayout.allRowObjs;
    const spatialIndex = viewportLayout.spatialIndex;
    const { offsetX: x, offsetY: y } = event as MouseEvent;
    const inDataBody = !(x < dbx || x > dbx + dbw || y < dby || y > dby + dbh);
    if (!inDataBody) return;

    const timePoint = {
      time: getPositionTime(x, viewportStartTime, viewportEndTime, width, isRTL),
      y: y + viewportYOffset
    };
    const targetIndices = queryTasksSpatialIndex(rowObjs, spatialIndex.tasks, timePoint);
    if (!targetIndices) return;

    const { rowIndex, taskIndex } = targetIndices;
    return rowObjs[rowIndex].taskObjs[taskIndex].id;
  };

  const onPointerMove = (event: PointerEvent) => {
    const id = idExtracter(event);
    if (id !== hoverProps.hoveredTaskId) {
      hoverProps.onTaskHoverChange({ id });
    }
    selectionSupported &&
      onCursorChange?.({ cursor: id !== undefined ? 'pointer' : 'pointerUnset' });
  };

  const onPointerUp = (event: PointerEvent) => {
    const id = idExtracter(event);
    if (id !== undefined) {
      selectionSupported && onCursorChange?.({ cursor: 'pointer' });
      if (id !== currentProps?.currentTask?.id) {
        currentProps.onCurrentTaskChange({ id, isFocused: false });
      }
    }
  };

  const hoverPointerGestureProps = { onPointerMove };
  const focusPointerGestureProps = { onPointerUp };

  const { onKeyUp, ...selectionPointerGestureProps } = useSelection<K2>({
    idExtracter,
    selection: selectionProps.selectedIds,
    selectionMode: selectionProps.selectionMode,
    onChange: selectionProps.onSelectionChange
  });

  const gestureProps = mergeProps(
    hoverPointerGestureProps,
    focusPointerGestureProps,
    selectionPointerGestureProps
  );

  return { gestureProps };
};

export { useGanttTaskPointerGestures };
